(function () {
  'use strict';

  var DEFAULT_OPTIONS = Object.freeze({
    hoverDwellMs: 80,
    maxPrefetches: 4,
    feedbackTimeoutMs: 4000
  });

  function attribute(element, name) {
    return element && typeof element.getAttribute === 'function' ? element.getAttribute(name) : null;
  }

  function hasAttribute(element, name) {
    return element && typeof element.hasAttribute === 'function' && element.hasAttribute(name);
  }

  function inferSourcePageType(context) {
    if (context.sourcePageType === 'index' || context.sourcePageType === 'collection') return context.sourcePageType;
    var pathname = new context.URL(context.location.href).pathname;
    return pathname === '/' ? 'index' : (/^\/collections\/[^/]+\/?$/.test(pathname) ? 'collection' : null);
  }

  function isNativeGetAnchor(anchor) {
    if (!anchor) return true;
    if (String(anchor.tagName || '').toUpperCase() !== 'A' || !hasAttribute(anchor, 'href')) return false;
    if (attribute(anchor, 'target') || hasAttribute(anchor, 'download') || hasAttribute(anchor, 'data-no-instant')) return false;
    return !attribute(anchor, 'data-method') && !attribute(anchor, 'data-turbo-method') && !attribute(anchor, 'formmethod') && !attribute(anchor, 'formaction') && !attribute(anchor, 'form');
  }

  function classifyEligibleNavigation(rawHref, context) {
    if (!context || !context.location || !context.URL || typeof rawHref !== 'string' || !rawHref || /[\u0000-\u001F\s]/.test(rawHref)) return null;
    if (!isNativeGetAnchor(context.anchor)) return null;

    var base;
    var destination;
    try {
      base = new context.URL(context.location.href);
      destination = new context.URL(rawHref, base.href);
    } catch (_error) {
      return null;
    }

    if (base.protocol !== 'https:' || destination.protocol !== base.protocol || destination.origin !== base.origin) return null;
    if (destination.search || destination.hash || rawHref.indexOf('?') !== -1 || rawHref.indexOf('#') !== -1) return null;

    var kind = /^\/collections\/[^/]+\/?$/.test(destination.pathname) ? 'collection' : (/^(?:\/products\/[^/]+|\/collections\/[^/]+\/products\/[^/]+)\/?$/.test(destination.pathname) ? 'product' : null);
    var sourcePageType = inferSourcePageType(context);
    if (!kind || !sourcePageType || (sourcePageType === 'collection' && kind !== 'product')) return null;
    return { kind: kind, href: destination.href };
  }

  function closestAnchor(target) {
    return target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
  }

  function storeAttribute(element, name) {
    return { present: hasAttribute(element, name), value: attribute(element, name) };
  }

  function restoreAttribute(element, name, previous) {
    if (previous.present) element.setAttribute(name, previous.value);
    else element.removeAttribute(name);
  }

  function createIntentPrefetchController(environment, _options) {
    if (!environment || !environment.document || !environment.location || !environment.navigator || !environment.URL || !environment.setTimeout || !environment.clearTimeout) throw new TypeError('Intent prefetch environment is incomplete.');

    var document = environment.document;
    var pageTarget = environment.window || document.defaultView || document;
    var timer = null;
    var timerAnchor = null;
    var started = false;
    var prefetchUnsupported = false;
    var prefetchedUrls = new Set();
    var ownedHints = [];
    var pending = null;

    function contextFor(anchor) {
      return { document: document, location: environment.location, URL: environment.URL, sourcePageType: environment.sourcePageType, anchor: anchor };
    }

    function hasNetworkOrLifecycleConstraint() {
      var connection = environment.navigator.connection || environment.navigator.mozConnection || environment.navigator.webkitConnection || {};
      return Boolean(connection.saveData || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g' || document.hidden || document.visibilityState === 'hidden' || (document.body && document.body.dataset && document.body.dataset.instantIntensity));
    }

    function supportsPrefetch() {
      if (prefetchUnsupported) return false;
      try {
        var probe = document.createElement('link');
        var supported = Boolean(probe && probe.relList && typeof probe.relList.supports === 'function' && probe.relList.supports('prefetch'));
        if (!supported) prefetchUnsupported = true;
        return supported;
      } catch (_error) {
        prefetchUnsupported = true;
        return false;
      }
    }

    function prefetch(anchor) {
      var navigation = classifyEligibleNavigation(attribute(anchor, 'href'), contextFor(anchor));
      if (!navigation || hasNetworkOrLifecycleConstraint() || prefetchedUrls.size >= DEFAULT_OPTIONS.maxPrefetches || prefetchedUrls.has(navigation.href) || !supportsPrefetch()) return;
      try {
        var hint = document.createElement('link');
        hint.setAttribute('rel', 'prefetch');
        hint.setAttribute('href', navigation.href);
        hint.setAttribute('data-winky-prefetch', '');
        document.head.appendChild(hint);
        prefetchedUrls.add(navigation.href);
        ownedHints.push(hint);
      } catch (_error) {
        prefetchUnsupported = true;
      }
    }

    function cancelDwell(anchor) {
      if (timer !== null && (!anchor || timerAnchor === anchor)) {
        environment.clearTimeout(timer);
        timer = null;
        timerAnchor = null;
      }
    }

    function schedulePrefetch(anchor) {
      cancelDwell();
      if (!anchor) return;
      timerAnchor = anchor;
      timer = environment.setTimeout(function () {
        var scheduledAnchor = timerAnchor;
        timer = null;
        timerAnchor = null;
        prefetch(scheduledAnchor);
      }, DEFAULT_OPTIONS.hoverDwellMs);
    }

    function clearPending() {
      if (!pending) return;
      environment.clearTimeout(pending.timeout);
      if (pending.pendingAttribute.present) pending.anchor.dataset.winkyNavigationPending = pending.pendingAttribute.value;
      else delete pending.anchor.dataset.winkyNavigationPending;
      restoreAttribute(pending.anchor, 'aria-busy', pending.ariaBusy);
      restoreAttribute(pending.anchor, 'data-winky-pending-positioned', pending.positioned);
      if (!pending.hadPendingClass) pending.anchor.classList.remove('winky-navigation-pending');
      pending = null;
    }

    function markPending(anchor) {
      clearPending();
      var positioned = storeAttribute(anchor, 'data-winky-pending-positioned');
      var computedStyle = environment.getComputedStyle || (pageTarget && pageTarget.getComputedStyle);
      if (typeof computedStyle === 'function') {
        try {
          if (computedStyle(anchor).position && computedStyle(anchor).position !== 'static') anchor.setAttribute('data-winky-pending-positioned', 'true');
        } catch (_error) {}
      }
      pending = {
        anchor: anchor,
        ariaBusy: storeAttribute(anchor, 'aria-busy'),
        positioned: positioned,
        pendingAttribute: { present: Object.prototype.hasOwnProperty.call(anchor.dataset, 'winkyNavigationPending'), value: anchor.dataset.winkyNavigationPending },
        hadPendingClass: anchor.classList.contains('winky-navigation-pending'),
        timeout: null
      };
      anchor.dataset.winkyNavigationPending = 'true';
      anchor.setAttribute('aria-busy', 'true');
      anchor.classList.add('winky-navigation-pending');
      pending.timeout = environment.setTimeout(clearPending, DEFAULT_OPTIONS.feedbackTimeoutMs);
    }

    function onPointerOver(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      var anchor = closestAnchor(event.target);
      if (anchor && anchor === closestAnchor(event.relatedTarget)) return;
      schedulePrefetch(anchor);
    }

    function onPointerOut(event) {
      var anchor = closestAnchor(event.target);
      if (anchor && (!event.relatedTarget || !anchor.contains(event.relatedTarget))) cancelDwell(anchor);
    }

    function onFocusIn(event) { schedulePrefetch(closestAnchor(event.target)); }

    function onFocusOut(event) {
      var anchor = closestAnchor(event.target);
      if (anchor && (!event.relatedTarget || !anchor.contains(event.relatedTarget))) cancelDwell(anchor);
    }

    function onPointerDown(event) {
      if (event.button !== 0 || event.isPrimary === false || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) return;
      cancelDwell();
      prefetch(closestAnchor(event.target));
    }

    function onClick(event) {
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      var anchor = closestAnchor(event.target);
      if (classifyEligibleNavigation(attribute(anchor, 'href'), contextFor(anchor))) markPending(anchor);
    }

    function add(target, type, callback) { target.addEventListener(type, callback, { passive: true }); }
    function remove(target, type, callback) { target.removeEventListener(type, callback, { passive: true }); }

    return {
      start: function () {
        if (started) return;
        started = true;
        add(document, 'pointerover', onPointerOver);
        add(document, 'pointerout', onPointerOut);
        add(document, 'focusin', onFocusIn);
        add(document, 'focusout', onFocusOut);
        add(document, 'pointerdown', onPointerDown);
        add(document, 'click', onClick);
        add(pageTarget, 'pageshow', clearPending);
        add(pageTarget, 'pagehide', clearPending);
      },
      stop: function () {
        if (!started) return;
        started = false;
        remove(document, 'pointerover', onPointerOver);
        remove(document, 'pointerout', onPointerOut);
        remove(document, 'focusin', onFocusIn);
        remove(document, 'focusout', onFocusOut);
        remove(document, 'pointerdown', onPointerDown);
        remove(document, 'click', onClick);
        remove(pageTarget, 'pageshow', clearPending);
        remove(pageTarget, 'pagehide', clearPending);
        cancelDwell();
        clearPending();
        ownedHints.forEach(function (hint) { if (hint && typeof hint.remove === 'function') hint.remove(); });
        ownedHints = [];
        prefetchedUrls.clear();
      }
    };
  }

  var api = { DEFAULT_OPTIONS: DEFAULT_OPTIONS, classifyEligibleNavigation: classifyEligibleNavigation, createIntentPrefetchController: createIntentPrefetchController };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else if (typeof document !== 'undefined' && document.body && document.body.dataset && document.body.dataset.winkyIntentPrefetch === 'enabled') {
    createIntentPrefetchController({ document: document, window: window, location: window.location, navigator: window.navigator, URL: window.URL, setTimeout: window.setTimeout.bind(window), clearTimeout: window.clearTimeout.bind(window), getComputedStyle: window.getComputedStyle.bind(window) }).start();
  }
}());
